// quoteLogger.js
// Google Sheets as quote database via Google Sheets API v4
// Requires: REACT_APP_GOOGLE_SHEETS_ID and REACT_APP_GOOGLE_API_KEY in .env
// Sheet name: "QuoteLog" with headers in row 1

// Our own addresses. A quote email sent only to these is a test send, so it
// never moves the quote's status or marks it as emailed to the client.
// Preview / local deployments (anything but the live app): new quotes start
// ticked as test quotes there, since previews write to the live sheet.
export const IS_PREVIEW_DEPLOYMENT = typeof window !== "undefined" && window.location.hostname !== "app.sublimeprecision.co.uk";

// A test quote can only go to our own addresses.
export const TEST_QUOTE_SEND_BLOCKED = "Test quote: only internal addresses. Untick Test quote to send to a client.";

export const INTERNAL_EMAILS = [
  "edward@sublimeprecision.co.uk",
  "james@sublimeprecision.co.uk",
  "edwardbuchanan79@gmail.com",
  "edward@wayoutwest.build",
];

const SHEET_ID = process.env.REACT_APP_GOOGLE_SHEETS_ID;
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const SHEET_NAME = "QuoteLog";

// Calls to our own API (/api/...) carry the signed-in user's Clerk session
// token, which the server checks (api/_lib/auth.js). Public pages don't use
// this. No session = no header (the server logs it while warn-only).
// Logs "[timing]" lines to the console (sign-in token, then the request), to
// find which step makes a save slow. Log only.
export async function apiFetch(url, opts = {}) {
  const t0 = performance.now();
  let token = null;
  try { token = await window.Clerk?.session?.getToken(); } catch (e) { /* not signed in */ }
  const t1 = performance.now();
  const headers = { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const res = await fetch(url, { ...opts, headers });
  let action = "";
  try { action = JSON.parse(opts.body || "{}").action || ""; } catch (e) { /* not JSON */ }
  console.info(`[timing] ${url}${action ? ` ${action}` : ""}: token ${Math.round(t1 - t0)} ms, request ${Math.round(performance.now() - t1)} ms`);
  return res;
}

// Column mapping (A→Z)
export const COLUMNS = {
  QUOTE_NUMBER: "A",
  DATE_CREATED: "B",
  DATE_UPDATED: "C",
  CLIENT_NAME: "D",
  CLIENT_EMAIL: "E",
  CLIENT_PHONE: "F",
  CATEGORY: "G",       // Industrial / Automotive
  APPLICATION: "H",
  SITE_ADDRESS: "I",
  STATUS: "J",
  TOTAL_EX_VAT: "K",
  TOTAL_INC_VAT: "L",
  MARGIN: "M",
  MILEAGE: "N",
  MILEAGE_COST: "O",
  NOTES: "P",
  AMENDMENT_OF: "Q",   // Original quote number if this is an amendment
  PDF_GENERATED: "R",  // "Yes" / "No"
  EMAIL_SENT: "S",     // "Yes" / "No"
  SCOPE_SUMMARY: "T",  // First 200 chars of scope
  IS_CURRENT: "V",     // "Yes" / "No" - "No" once superseded by an amendment
  SUPERSEDED_BY: "W",  // Quote number of the amendment that superseded this row
  PO_DATA: "X",        // JSON blob - dry-ice Purchase Order fields (see savePODetails)
  TEST_QUOTE: "Y",     // "Yes" = test quote, kept out of dashboard figures
  DATE_SENT: "Z",      // Most recent send to the client (real email or Mark as sent), dd/mm/yyyy
  SAVE_ID: "AA",       // One per save attempt; a retried save finds its row by it (see writeWithSaveCheck)
  UPDATED_AT: "AB",    // Date + time of the last change (ISO), written with every Date Updated (C) - Quote Log tie-break
  LOSS_REASON: "AC",   // Why a Lost quote was lost (one of LOSS_REASONS in workflow.js), for filtering and counting
  LOSS_NOTE: "AD",     // Optional note typed with the loss reason
};

// Statuses a quote can be given. Automotive goes Accepted → Deposit pending
// → Deposit paid → Complete; Industrial/Marine Accepted → Contracted →
// Invoiced → Complete. "Rejected" is the old name for Lost: older rows may
// still hold it (see STATUS_COLORS), but it's no longer offered.
export const STATUSES = [
  "Draft",
  "Sent",
  "Amended",
  "Accepted",
  "Deposit pending",
  "Deposit paid",
  "Contracted",
  "Invoiced",
  "Complete",
  "Lost",
];

export const STATUS_COLORS = {
  Draft:    { bg: "#1D3461", text: "#ffffff", border: "#3a52cc" },
  Sent:     { bg: "#7B5800", text: "#FFD966", border: "#F5A623" },
  Amended:  { bg: "#3D1A5C", text: "#D9A8FF", border: "#9C4DCC" },
  Accepted:    { bg: "#0D5C36", text: "#7DEBB0", border: "#27AE60" },
  Contracted:  { bg: "#1a1a4e", text: "#a0b4ff", border: "#3a52cc" },
  "Deposit pending": { bg: "#7B5800", text: "#FFD966", border: "#F5A623" },
  "Deposit paid":    { bg: "#1a1a4e", text: "#a0b4ff", border: "#3a52cc" },
  Lost:     { bg: "#8B1A1A", text: "#FFB3B3", border: "#E53935" },
  Rejected: { bg: "#8B1A1A", text: "#FFB3B3", border: "#E53935" },
  Invoiced: { bg: "#0A4A7A", text: "#90CAF9", border: "#2196F3" },
  Complete: { bg: "#2A2A2A", text: "#AAAAAA", border: "#666666" },
};

const HEADERS = [
  "Quote Number", "Date Created", "Date Updated", "Client Name",
  "Client Email", "Client Phone", "Category", "Application",
  "Site Address", "Status", "Total (ex VAT)", "Total (inc VAT)",
  "Margin %", "Mileage", "Mileage Cost", "Notes",
  "Amendment Of", "PDF Generated", "Email Sent", "Scope Summary", "State JSON",
  "Is Current", "Superseded By", "PO Data", "Test Quote", "Date Sent",
  "Save ID", "Updated At", "Loss Reason", "Loss Note",
];

// ─── Fetch all quotes ────────────────────────────────────────────────────────
export async function fetchAllQuotes() {
  if (!SHEET_ID || !API_KEY) {
    throw new Error("Missing REACT_APP_GOOGLE_SHEETS_ID or REACT_APP_GOOGLE_API_KEY");
  }

  const range = `${SHEET_NAME}!A1:AD1000`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${encodeURIComponent(range)}?key=${API_KEY}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Couldn't read the Quote Log (error ${res.status}).`);

  const data = await res.json();
  const rows = data.values || [];

  if (rows.length <= 1) return []; // Only headers or empty

  const headers = rows[0];
  return rows.slice(1).map((row, i) => {
    const obj = {};
    headers.forEach((h, j) => { obj[h] = row[j] || ""; });
    // By position too: these columns' headers are written by the Apps Script
    // (Code.gs) and may not be there yet.
    if (!headers.includes("Updated At")) obj["Updated At"] = row[27] || "";
    if (!headers.includes("Loss Reason")) obj["Loss Reason"] = row[28] || "";
    if (!headers.includes("Loss Note")) obj["Loss Note"] = row[29] || "";
    obj._rowIndex = i + 2; // 1-based, +1 for header row
    return obj;
  });
}

// ─── Append a new quote row ──────────────────────────────────────────────────
// New quotes get their final number from the Apps Script ("appendQuote" in
// Code.gs), assigned under a lock from a stored high-water mark per prefix
// and year - never from this browser's counter, which other devices don't
// share and which resets if browser data is cleared. quoteData.quoteNumber
// is only the provisional number shown while editing; the result's
// quoteNumber is the real one. Until the updated Code.gs is deployed the
// script answers "Unknown action", and this falls back to the old plain
// append so saving keeps working.
export async function appendQuote(quoteData, opts) {
  return writeWithSaveCheck(quoteData, () => appendQuoteOnce(quoteData), opts);
}
async function appendQuoteOnce(quoteData) {
  const SERVICE_URL = "/api/sheets-proxy";
  const row = buildRow(quoteData);
  const m = String(quoteData.quoteNumber || "").match(/^([A-Z]+)-(\d{4})-/);
  const post = async (body) => {
    const res = await apiFetch(SERVICE_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
    return await res.json();
  };
  if (m) {
    const result = await post({ action: "appendQuote", sheetName: SHEET_NAME, row, prefix: m[1], year: m[2], saveId: quoteData.saveId || "" });
    if (result.success) return result;
    if (!/Unknown action/.test(result.error || "")) throw new Error(result.error || "Saving the quote failed");
  }
  const result = await post({ action: "append", sheetName: SHEET_NAME, row });
  return { ...result, quoteNumber: quoteData.quoteNumber };
}

// ─── Saves that can't double up ──────────────────────────────────────────────
// Each save carries a saveId (quoteData.saveId, written to column AA). A slow
// or lost reply doesn't mean the row wasn't written, so before saying a save
// failed we look for its saveId in the sheet: found = it went through. A
// retry of the same save reuses the saveId, and the Apps Script returns the
// row it already wrote instead of adding another.
export const newSaveId = () =>
  (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const SAVE_SLOW_MS = 8000;     // then "Still saving… checking whether it went through"
const SAVE_REPLY_MS = 25000;   // no reply by then: stop waiting and check the sheet
const SAVE_CHECK_WAITS = [0, 4000, 8000];

// Previews only, for testing the slow path: localStorage
// "sublime_test_save_reply" = "ignore" (the row is written but its reply is
// ignored, as if it never came) or "drop" (nothing is written, no reply).
// Either way the save then times out and checks the sheet, on shorter
// timings so it's quick to test.
const SAVE_TEST_TIMINGS = { slow: 2000, reply: 8000 };
export function saveTestMode() {
  if (!IS_PREVIEW_DEPLOYMENT) return "";
  try { return window.localStorage.getItem("sublime_test_save_reply") || ""; } catch (e) { return ""; }
}

async function findRowBySaveId(saveId) {
  const range = `${SHEET_NAME}!A2:AA1000`;
  const res = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${encodeURIComponent(range)}?key=${API_KEY}`);
  if (!res.ok) throw new Error(`Couldn't read the Quote Log (error ${res.status}).`);
  const rows = (await res.json()).values || [];
  const i = rows.findIndex(r => r[26] === saveId);
  return i === -1 ? null : { quoteNumber: rows[i][0], rowIndex: i + 2 };
}

// opts.onSlow: called once if the save is taking a while or needs checking.
async function writeWithSaveCheck(quoteData, write, { onSlow } = {}) {
  const saveId = quoteData.saveId;
  const mode = saveTestMode();
  if (mode) console.info(`Save test switch on: ${mode}`);
  const timing = mode ? SAVE_TEST_TIMINGS : { slow: SAVE_SLOW_MS, reply: SAVE_REPLY_MS };
  let slowShown = false;
  const slow = () => { if (!slowShown && onSlow) { slowShown = true; onSlow(); } };
  const slowTimer = setTimeout(slow, timing.slow);
  let timeoutId;
  try {
    const timeout = new Promise((_, reject) => { timeoutId = setTimeout(() => reject(new Error(mode ? `Test switch "${mode}": no reply` : "No reply from the Quote Log")), timing.reply); });
    let reply;
    if (mode === "drop") reply = new Promise(() => {});                                   // nothing sent
    else if (mode === "ignore") { write().catch(() => {}); reply = new Promise(() => {}); } // sent, reply never used
    else reply = write();
    return await Promise.race([reply, timeout]);
  } catch (e) {
    if (!saveId) throw e;
    slow();
    for (const wait of SAVE_CHECK_WAITS) {
      if (wait) await new Promise(r => setTimeout(r, wait));
      try {
        const found = await findRowBySaveId(saveId);
        if (found) return { success: true, ...found, recovered: true };
      } catch (checkErr) { /* sheet unreadable this time - try again */ }
    }
    const err = new Error(`Not saved, try again. (${e.message})`);
    err.notSaved = true;
    throw err;
  } finally {
    clearTimeout(slowTimer);
    clearTimeout(timeoutId);
  }
}

// ─── Append an amendment row ─────────────────────────────────────────────────
// Unlike appendQuote(), the row's own quote number (quoteData.quoteNumber) is
// only a provisional display value - the Apps Script computes the real one
// itself, locked, from the sheet's actual current state at write time, and
// also marks the original row (quoteData.amendmentOf) Is Current=No /
// Superseded By=<new number> in the same locked pass. This is what closes the
// race condition generateAmendmentNumber() had on its own (two amendments
// started close together could both compute "next letter is a" from stale
// client-side state and collide). Returns the server-assigned quoteNumber -
// callers should use that, not quoteData.quoteNumber, since it can differ
// from what was shown while editing if a real race was actually resolved.
export async function appendAmendment(quoteData, opts) {
  return writeWithSaveCheck(quoteData, () => appendAmendmentOnce(quoteData), opts);
}
async function appendAmendmentOnce(quoteData) {
  const SERVICE_URL = "/api/sheets-proxy";
  if (!SERVICE_URL) throw new Error("Missing REACT_APP_SHEETS_PROXY_URL (needed for writes)");
  if (!quoteData.amendmentOf) throw new Error("appendAmendment requires amendmentOf");

  const row = buildRow(quoteData);
  const res = await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "appendAmendment", sheetName: SHEET_NAME, row, amendmentOf: quoteData.amendmentOf, saveId: quoteData.saveId || "" }),
  });

  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  return await res.json(); // { success, quoteNumber, rowIndex, originalRowIndex }
}

// ─── Update a specific row (by rowIndex) ────────────────────────────────────
export async function updateQuoteRow(rowIndex, quoteData) {
  const SERVICE_URL = "/api/sheets-proxy";
  if (!SERVICE_URL) throw new Error("Missing REACT_APP_SHEETS_PROXY_URL");

  // A–W only: never overwrite an existing row's PO Data (X) or test flag (Y).
  const row = buildRow(quoteData).slice(0, 23);
  const res = await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "update", sheetName: SHEET_NAME, rowIndex, row }),
  });

  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  return await res.json();
}

// ─── Update status only ──────────────────────────────────────────────────────
// extraCells: more cells written in the same call, e.g. the loss reason.
export async function updateQuoteStatus(rowIndex, newStatus, extraCells = []) {
  const SERVICE_URL = "/api/sheets-proxy";
  if (!SERVICE_URL) throw new Error("Missing REACT_APP_SHEETS_PROXY_URL");

  const now = new Date().toLocaleDateString("en-GB");
  // Update columns J (Status) and C (Date Updated) only
  const res = await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: "updateCells",
      sheetName: SHEET_NAME,
      rowIndex,
      updatedAt: new Date().toISOString(), // AB: Updated At (written by Code.gs)
      cells: [
        { col: "C", value: now },     // Date Updated
        { col: "J", value: newStatus }, // Status
        ...extraCells,
      ],
    }),
  });

  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  return await res.json();
}

// ─── Log a manual status change (audit trail) ───────────────────────────────
// Separate sheet tab, written via the same generic "append"/"ensureHeaders"
// actions the QuoteLog sheet uses - doPost() creates the tab itself on first
// write if it doesn't exist yet, so no Apps Script changes are needed for
// this. Manual dropdown changes log here with their reason, and so does the
// automatic move to Sent after a client send (recordClientSend). The GoodSign
// webhook (Accepted -> Contracted) doesn't call this.
// ─── Send log ────────────────────────────────────────────────────────────────
// One row per quote email actually sent (Save & Email or Send to Client):
// who it went to and when. Kept separate from the QuoteLog row on purpose -
// sending never changes the quote's own client details, which stay the
// master record.
const SEND_LOG_SHEET = "SendLog";

// "YYYY-MM-DD HH:MM:SS" in UK time (Europe/London, so BST/GMT is handled) -
// readable in the sheet and still sorts correctly as text.
export function ukTimestamp(date = new Date()) {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(date).map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
}

// SendLog timestamps back to "YYYY-MM-DD HH:MM:SS" UK time, whatever form
// they come back in: early rows were UTC ISO ("...T13:05:00.000Z"), and Sheets
// may store our text as a date-time and return it formatted ("01/10/2026 14:05:23").
export function normaliseTimestamp(raw) { return normaliseSendTimestamp(raw); }
function normaliseSendTimestamp(raw) {
  const s = String(raw || "").trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) { const d = new Date(s); return isNaN(d) ? s : ukTimestamp(d); }
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})[ ,]+(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")} ${m[4].padStart(2, "0")}:${m[5]}:${m[6] || "00"}`;
  m = s.match(/^(\d{4}-\d{2}-\d{2}) (\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m) return `${m[1]} ${m[2].padStart(2, "0")}:${m[3]}:${m[4] || "00"}`;
  return s;
}

// "1 Oct 2026, 14:05" for display - from a normalised UK timestamp, without
// converting time zones again.
export function formatUkTimestamp(ts) {
  // ISO timestamps ("2026-10-21T18:53:48.918Z", UTC) → UK local time, e.g.
  // "21 Oct 2026, 19:53". Older rows use "YYYY-MM-DD HH:MM", handled below.
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(ts || ""))) {
    const d = new Date(ts);
    if (!isNaN(d.getTime())) {
      const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(d).map(x => [x.type, x.value]));
      return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute}`;
    }
  }
  const m = String(ts || "").match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/);
  if (!m) return ts || "";
  const month = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(m[2]) - 1];
  return `${Number(m[3])} ${month} ${m[1]}, ${m[4]}:${m[5]}`;
}
const SEND_LOG_HEADERS = ["Timestamp", "Quote Number", "Sent To", "BCC", "Sent By", "Method"];

export async function logQuoteSend({ quoteNumber, to, bcc, sentBy, method }) {
  const SERVICE_URL = "/api/sheets-proxy";
  await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "ensureHeaders", sheetName: SEND_LOG_SHEET, headers: SEND_LOG_HEADERS }),
  });
  const entry = { timestamp: ukTimestamp(), quoteNumber: quoteNumber || "", to: to || "", bcc: (bcc || []).join(", "), sentBy: sentBy || "", method: method || "" };
  const row = [entry.timestamp, entry.quoteNumber, entry.to, entry.bcc, entry.sentBy, entry.method];
  const res = await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "append", sheetName: SEND_LOG_SHEET, row }),
  });
  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  return entry;
}

// All logged sends, oldest first. A missing SendLog sheet (nothing sent
// since it was introduced) is just an empty log.
export async function fetchSendLog() {
  if (!SHEET_ID || !API_KEY) return [];
  const range = `${SEND_LOG_SHEET}!A2:F5000`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${encodeURIComponent(range)}?key=${API_KEY}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json();
  return (data.values || []).map(r => ({ timestamp: normaliseSendTimestamp(r[0]), quoteNumber: r[1] || "", to: r[2] || "", bcc: r[3] || "", sentBy: r[4] || "", method: r[5] || "" }));
}

// Most recent send for a quote number, or null.
// "(on file)" rows only record the ID of a document that was already out
// (see api/contracts.js recordPendingIds): nothing was sent at that time, so
// they never count as a send.
export const isOnFileRow = e => / \(on file\)$/.test(e?.method || "");

export function lastSendFor(sendLog, quoteNumber) {
  let last = null;
  (sendLog || []).forEach(e => { if (e.quoteNumber === quoteNumber && !isOnFileRow(e) && (!last || e.timestamp > last.timestamp)) last = e; });
  return last;
}

// For a version that hasn't been sent yet (Draft, or Amended from before
// amendments saved as Draft): the version the client actually has - the
// latest other version of the same quote that reached them - and the day it
// was sent. { number, status, date: "2 Oct" } or null.
const REACHED_CLIENT = ["Sent", "Accepted", "Deposit pending", "Deposit paid", "Contracted", "Invoiced", "Complete", "Lost", "Rejected"];
export function versionClientHas(q, quotes, sendLog) {
  if (!q || !["Draft", "Amended"].includes(q["Status"])) return null;
  const qn = q["Quote Number"];
  const base = String(qn || "").replace(/[a-z]+$/, "");
  const suffix = n => String(n).slice(base.length);
  const sent = (quotes || []).filter(x => {
    const n = String(x["Quote Number"] || "");
    return n !== qn && n.replace(/[a-z]+$/, "") === base && (REACHED_CLIENT.includes(x["Status"]) || !!x["Date Sent"]);
  }).sort((a, b) => suffix(a["Quote Number"]).localeCompare(suffix(b["Quote Number"])));
  const v = sent[sent.length - 1];
  if (!v) return null;
  const last = lastSendFor(sendLog, v["Quote Number"]);
  const when = parseDate(v["Date Sent"])
    || (last?.timestamp ? new Date(String(last.timestamp).replace(" ", "T")) : null)
    || parseDate(v["Date Updated"]);
  const date = when && !isNaN(when) ? when.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
  return { number: v["Quote Number"], status: v["Status"] || "Sent", date };
}
// "Client has 027b (Sent 2 Oct)" - the wording for versionClientHas.
export function clientHasLabel(has, short = n => String(n || "").split("-").pop()) {
  if (!has) return "";
  const sentPart = has.status === "Sent" ? `Sent${has.date ? ` ${has.date}` : ""}` : `${has.status}${has.date ? `, sent ${has.date}` : ""}`;
  return `Client has ${short(has.number)} (${sentPart})`;
}

const STATUS_LOG_SHEET = "StatusChangeLog";
const STATUS_LOG_HEADERS = ["Quote Number", "Timestamp", "Changed By", "From Status", "To Status", "Reason"];

// All logged status changes (for a quote's History), oldest first, with
// timestamps in the same UK form as the SendLog. Missing sheet = empty.
export async function fetchStatusLog() {
  if (!SHEET_ID || !API_KEY) return [];
  const range = `${STATUS_LOG_SHEET}!A2:F5000`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${encodeURIComponent(range)}?key=${API_KEY}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json();
  return (data.values || []).map(r => ({ quoteNumber: r[0] || "", timestamp: normaliseSendTimestamp(r[1]), changedBy: r[2] || "", fromStatus: r[3] || "", toStatus: r[4] || "", reason: r[5] || "" }));
}

// The header check runs once per session, not before every line.
let _statusLogHeadersDone = false;
export async function logStatusChange({ quoteNumber, changedBy, fromStatus, toStatus, reason }) {
  const SERVICE_URL = "/api/sheets-proxy";
  if (!SERVICE_URL) throw new Error("Missing REACT_APP_SHEETS_PROXY_URL");

  if (!_statusLogHeadersDone) {
    const h = await apiFetch(SERVICE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "ensureHeaders", sheetName: STATUS_LOG_SHEET, headers: STATUS_LOG_HEADERS }),
    });
    if (h.ok) _statusLogHeadersDone = true;
  }

  const row = [quoteNumber || "", new Date().toISOString(), changedBy || "", fromStatus || "", toStatus || "", reason || ""];
  const res = await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "append", sheetName: STATUS_LOG_SHEET, row }),
  });

  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  return await res.json();
}

// Loss Reason / Loss Note headers (AC1, AD1), written once per session
// before the first Lost is saved (same reason as ensureDateSentHeader).
let _lossHeadersDone = false;
async function ensureLossHeaders() {
  if (_lossHeadersDone) return;
  const res = await apiFetch("/api/sheets-proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "updateCells", sheetName: SHEET_NAME, rowIndex: 1, cells: [{ col: COLUMNS.LOSS_REASON, value: "Loss Reason" }, { col: COLUMNS.LOSS_NOTE, value: "Loss Note" }] }),
  });
  if (res.ok) _lossHeadersDone = true;
}

// Mark a quote Lost: Status, Loss Reason (AC) and Loss Note (AD) in one
// write, then a History line "Sent → Lost: Price, <note>". Returns the patch
// for the local row.
export async function markQuoteLost({ quote, reason, note = "", changedBy, historyReason }) {
  await ensureLossHeaders();
  const cleanNote = String(note || "").trim();
  await updateQuoteStatus(quote._rowIndex, "Lost", [
    { col: COLUMNS.LOSS_REASON, value: reason },
    { col: COLUMNS.LOSS_NOTE, value: cleanNote },
  ]);
  try {
    await logStatusChange({
      quoteNumber: quote["Quote Number"], changedBy,
      fromStatus: quote["Status"], toStatus: "Lost",
      reason: historyReason || [reason, cleanNote].filter(Boolean).join(", "),
    });
  } catch (e) { console.error("Failed to write StatusChangeLog entry:", e); }
  return { Status: "Lost", "Loss Reason": reason, "Loss Note": cleanNote, "Date Updated": new Date().toLocaleDateString("en-GB") };
}

// The Date Sent header (Z1). ensureHeaders only writes a header row into an
// empty sheet, so a column added later gets its header here, once per session.
let _dateSentHeaderDone = false;
async function ensureDateSentHeader() {
  if (_dateSentHeaderDone) return;
  const res = await apiFetch("/api/sheets-proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "updateCells", sheetName: SHEET_NAME, rowIndex: 1, cells: [{ col: COLUMNS.DATE_SENT, value: "Date Sent" }] }),
  });
  if (res.ok) _dateSentHeaderDone = true;
}

// ─── Mark an existing row as emailed (no new row, no recalculation) ─────────
// Only called for real client sends (see recordClientSend), so Date Sent is
// the most recent send to the client; a re-send moves it on.
// status: also move Status (J) on, in the same write (see recordClientSend).
export async function markEmailSent(rowIndex, { status } = {}) {
  const SERVICE_URL = "/api/sheets-proxy";
  if (!SERVICE_URL) throw new Error("Missing REACT_APP_SHEETS_PROXY_URL");

  const now = new Date().toLocaleDateString("en-GB");
  await ensureDateSentHeader();
  const res = await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: "updateCells",
      sheetName: SHEET_NAME,
      rowIndex,
      updatedAt: new Date().toISOString(), // AB: Updated At (written by Code.gs)
      cells: [
        { col: "C", value: now },                    // Date Updated
        { col: COLUMNS.EMAIL_SENT, value: "Yes" },    // Email Sent
        { col: COLUMNS.DATE_SENT, value: now },       // Date Sent
        ...(status ? [{ col: "J", value: status }] : []), // Status
      ],
    }),
  });

  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  return await res.json();
}

// ─── After a quote email is sent ────────────────────────────────────────────
// True when every To address is one of ours (BCC doesn't count).
export function isInternalOnlySend(to) {
  const list = String(to || "").split(/[,;\s]+/).map(a => a.trim().toLowerCase()).filter(Boolean);
  return list.length > 0 && list.every(a => INTERNAL_EMAILS.includes(a));
}

// Statuses a client send moves on to Sent. Everything else (Sent, Accepted,
// Deposit pending/paid, Contracted, Invoiced, Complete, Lost) is left alone:
// forward only.
const MOVES_TO_SENT = ["Draft", "Amended"];

// Call once a quote email has actually gone out. For a send to the client
// (not just to us): Email Sent = Yes, and Draft/Amended -> Sent with a
// StatusChangeLog entry. Returns what the row now holds, so the caller can
// update its own copy without refetching.
// "Mark as sent" (Draft only): the quote went to the client some other way.
// Status -> Sent and Date Sent set; Email Sent stays as it was, nothing is
// emailed. Returns the date written.
export async function markQuoteSentManually({ rowIndex, quoteNumber, changedBy }) {
  const now = new Date().toLocaleDateString("en-GB");
  await ensureDateSentHeader();
  const res = await apiFetch("/api/sheets-proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: "updateCells",
      sheetName: SHEET_NAME,
      rowIndex,
      updatedAt: new Date().toISOString(), // AB: Updated At (written by Code.gs)
      cells: [
        { col: "C", value: now },                 // Date Updated
        { col: COLUMNS.STATUS, value: "Sent" },
        { col: COLUMNS.DATE_SENT, value: now },
      ],
    }),
  });
  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  try {
    await logStatusChange({ quoteNumber, changedBy, fromStatus: "Draft", toStatus: "Sent", reason: "Marked as sent (not emailed)" });
  } catch (e) { console.error("Failed to write StatusChangeLog entry:", e); }
  return now;
}

// Edit a quote's contact details in place (any status, current version
// only): the Quote Log columns and the same fields inside its State JSON, no
// new version and no price change. changes: { name, email, phone, address }.
// Logged to the StatusChangeLog (same status both sides) so it shows in History.
// A phone number as a sheet cell: a leading apostrophe makes Google Sheets
// keep it as text, so "07787 153084" or "07787153084" keeps its leading zero
// (without it the sheet turns 07787153084 into the number 7787153084). The
// apostrophe isn't shown in the sheet and isn't returned when it's read back.
export const phoneCell = v => { const s = String(v ?? "").trim(); return s ? "'" + s : ""; };

// A quote's client phone for showing and editing: the one saved with the
// quote's details (State JSON), which keeps its leading zero, falling back
// to the Client Phone column. Older rows can hold that column as a number
// that has lost its zero (e.g. 7787153084); those rows aren't changed.
export function quotePhone(q) {
  let st = null;
  try { st = q?.["State JSON"] ? JSON.parse(q["State JSON"]) : null; } catch (e) { /* no saved state */ }
  const saved = q?.["Category"] === "Automotive" ? st?.client?.phone : st?.job?.clientPhone;
  return String(saved || q?.["Client Phone"] || "").trim();
}

export async function updateContactDetails({ quote, changes, changedBy }) {
  const st = quote["State JSON"] ? JSON.parse(quote["State JSON"]) : null;
  if (!st) throw new Error("This quote has no saved details to update.");
  const isAuto = quote["Category"] === "Automotive";
  const next = isAuto
    ? { ...st, client: { ...(st.client || {}), name: changes.name, email: changes.email, phone: changes.phone, address: changes.address } }
    : { ...st, job: { ...(st.job || {}), clientName: changes.name, clientEmail: changes.email, clientPhone: changes.phone, clientAddress: changes.address } };
  const now = new Date().toLocaleDateString("en-GB");
  const cells = [
    { col: "C", value: now },
    { col: COLUMNS.CLIENT_NAME, value: changes.name },
    { col: COLUMNS.CLIENT_EMAIL, value: changes.email },
    { col: COLUMNS.CLIENT_PHONE, value: phoneCell(changes.phone) },
    { col: "U", value: JSON.stringify(next) },  // State JSON
  ];
  const res = await apiFetch("/api/sheets-proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "updateCells", sheetName: SHEET_NAME, rowIndex: quote._rowIndex, updatedAt: new Date().toISOString(), cells }),
  });
  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  const before = { name: quote["Client Name"], email: quote["Client Email"], phone: quotePhone(quote), address: isAuto ? st.client?.address : st.job?.clientAddress };
  const labels = { name: "name", email: "email", phone: "phone", address: "address" };
  const diff = Object.keys(labels).filter(k => (before[k] || "") !== (changes[k] || "")).map(k => `${labels[k]} ${before[k] || "(blank)"} → ${changes[k] || "(blank)"}`);
  try {
    await logStatusChange({ quoteNumber: quote["Quote Number"], changedBy, fromStatus: quote["Status"], toStatus: quote["Status"], reason: `Contact details edited: ${diff.join("; ") || "no change"}` });
  } catch (e) { console.error("Failed to write StatusChangeLog entry:", e); }
  return { ...quote, "Date Updated": now, "Client Name": changes.name, "Client Email": changes.email, "Client Phone": changes.phone, "State JSON": JSON.stringify(next) };
}

// testQuote: a Test quote can only ever be emailed to our own addresses, so
// for one of those an internal send counts as the client send (Sent, Email
// Sent, Date Sent) - test runs then behave like real ones. A real quote sent
// only to us still changes nothing.
export async function recordClientSend({ rowIndex, quoteNumber, currentStatus, to, changedBy, method, testQuote = false }) {
  const internal = isInternalOnlySend(to);
  if (!rowIndex || (internal && !testQuote)) return { emailSent: false, status: currentStatus };
  // One write for Email Sent, Date Sent and (Draft/Amended) Status, then
  // History: each round trip to the sheet can take a few seconds.
  const moves = MOVES_TO_SENT.includes(currentStatus);
  await markEmailSent(rowIndex, moves ? { status: "Sent" } : {});
  if (!moves) return { emailSent: true, status: currentStatus };
  try {
    await logStatusChange({ quoteNumber, changedBy, fromStatus: currentStatus, toStatus: "Sent", reason: internal ? `Sent by email (${method}; test quote, internal address)` : `Sent to client by email (${method})` });
  } catch (e) { console.error("Failed to write StatusChangeLog entry:", e); }
  return { emailSent: true, status: "Sent" };
}

// ─── Generate amendment quote number (provisional display only) ─────────────
// This is what's shown in the form while editing an amendment, computed from
// whatever quotes happened to be loaded into React state - it is NOT
// authoritative. appendAmendment() has the Apps Script recompute this fresh
// from the sheet itself, locked, at actual save time, since this client-side
// version has no way to see another amendment saved seconds ago in a
// different tab/session. If a genuine race occurs, the number saved can
// differ by a letter from what was shown here - appendAmendment()'s response
// carries the real one.
// The number the next amendment will get, for display before saving: one
// letter after the HIGHEST ever used for the base number, never the first
// gap (so a deleted version's letter, and its history, is never reused).
// Looks where the Apps Script looks: Quote Number, Amendment Of and
// Superseded By on every row, plus the SendLog and StatusChangeLog entries
// (history: [{ quoteNumber }]). The Apps Script also keeps a high-water mark
// and assigns the real number on save, so this can only ever be lower than
// the saved number if letters were used and then deleted everywhere.
// e.g. SBL-2026-001 → SBL-2026-001a; 027b (with 027a deleted) → 027c.
export function generateAmendmentNumber(originalNumber, existingQuotes, history = []) {
  const base = String(originalNumber || "").replace(/[a-z]+$/, "");
  const numbers = [
    ...(existingQuotes || []).flatMap(q => [q["Quote Number"], q["Amendment Of"], q["Superseded By"]]),
    ...(history || []).map(e => e?.quoteNumber),
    originalNumber,
  ];
  let highest = "";
  numbers.forEach(n => {
    const s = String(n || "").trim();
    if (!s.startsWith(base)) return;
    const suffix = s.slice(base.length);
    if (/^[a-z]$/.test(suffix) && suffix > highest) highest = suffix;
  });
  return base + (highest ? String.fromCharCode(highest.charCodeAt(0) + 1) : "a");
}

// ─── Ensure headers exist (first run) ───────────────────────────────────────
export async function ensureHeaders() {
  const SERVICE_URL = "/api/sheets-proxy";
  if (!SERVICE_URL) return;

  await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: "ensureHeaders",
      sheetName: SHEET_NAME,
      headers: HEADERS,
    }),
  });
}

// ─── Internal: build row array from quoteData object ─────────────────────────
function buildRow(q) {
  const now = new Date().toLocaleDateString("en-GB");
  return [
    q.quoteNumber || "",
    q.dateCreated || now,
    now,
    q.clientName || "",
    q.clientEmail || "",
    phoneCell(q.clientPhone),
    q.category || "",
    q.application || "",
    q.siteAddress || "",
    q.status || "Draft",
    q.totalExVat != null ? `£${Number(q.totalExVat).toFixed(2)}` : "",
    q.totalIncVat != null ? `£${Number(q.totalIncVat).toFixed(2)}` : "",
    q.margin != null ? `${q.margin}%` : "",
    q.mileage || "",
    q.mileageCost != null ? `£${Number(q.mileageCost).toFixed(2)}` : "",
    q.notes || "",
    q.amendmentOf || "",
    q.pdfGenerated ? "Yes" : "No",
    q.emailSent ? "Yes" : "No",
    q.scopeSummary ? q.scopeSummary.substring(0, 200) : "",
    q.stateJson ? JSON.stringify(q.stateJson) : "",
    q.isCurrent === false ? "No" : "Yes",
    q.supersededBy || "",
    "", // X: PO Data - a new row has none yet
    q.testQuote ? "Yes" : "",
    "", // Z: Date Sent - set on the first send
    q.saveId || "", // AA: Save ID
    new Date().toISOString(), // AB: Updated At
  ];
}

// Mark / unmark an existing row as a test quote (column Y only).
export async function setTestQuote(rowIndex, isTest) {
  const res = await apiFetch("/api/sheets-proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "updateCells", sheetName: SHEET_NAME, rowIndex, cells: [{ col: COLUMNS.TEST_QUOTE, value: isTest ? "Yes" : "" }] }),
  });
  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  return await res.json();
}

// ─── Send email via SendGrid proxy ──────────────────────────────────────────
// The person to greet in a quote email. Industrial keeps the contact in
// job.clientPOC (job.clientName is the company); Automotive's client.name is
// the person.
export function getQuoteContactName(stateJson) {
  return (stateJson?.job?.clientPOC || stateJson?.client?.name || "").trim();
}

// "Dear Dave," from the contact's first name (or "Dear Mr Smith," when the
// name starts with a title), falling back to the company/client name, then
// "Hello," if there's neither.
export function emailGreeting(contactName, clientName) {
  const contact = (contactName || "").trim();
  if (contact) {
    const parts = contact.split(/\s+/);
    if (parts.length > 1 && /^(mr|mrs|ms|miss|mx|dr|prof)\.?$/i.test(parts[0])) return `Dear ${contact},`;
    return `Dear ${parts[0].charAt(0).toUpperCase()}${parts[0].slice(1)},`;
  }
  const company = (clientName || "").trim();
  return company ? `Dear ${company},` : "Hello,";
}

// Public quote page on the site this is running on - app.sublimeprecision.co.uk
// in production, the preview's own URL on a preview - so "View Full Quote"
// always shows the same version of the quote template that sent it (it used to
// be hardcoded to the main-branch vercel.app deployment).
export function quotePageUrl(quoteNumber) {
  return `${window.location.origin}/quote/${encodeURIComponent(quoteNumber)}`;
}

// User-typed text going into the email HTML - escaped, line breaks kept.
function escapeEmailText(text) {
  return String(text)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
    .replace(/\r?\n/g, "<br>");
}

// Optional Automotive email blocks, ticked per send in the Save & Email /
// Send to Client dialogs. Only ticked blocks render.
export const AUTO_EMAIL_BLOCKS = [
  { key: "courtesyCar", label: "Courtesy car (996) block" },
  { key: "underbody", label: "Underbody protection block" },
];

// Send-dialog defaults for a quote's State JSON: underbody protection is
// offered by default only when the quote has no protection system on it;
// everything else starts off. (The Showcase Project rate is now a discount
// preset that pre-fills the Personal note - see discountPresets.js.)
export function defaultAutoEmailBlocks(stateJson) {
  const protection = stateJson?.protection;
  return { courtesyCar: false, underbody: !protection || protection === "none" };
}
export function getQuoteVehicleMake(stateJson) {
  return (stateJson?.vehicle?.make || "").trim();
}

// personalNote: optional free text from the send dialog, printed as its own
// paragraph above the quote summary box. Blank = the standard email.
// blocks: which optional Automotive blocks to include (see AUTO_EMAIL_BLOCKS);
// ignored for other categories. discount: resolveDiscount(...) result, shown
// as "Quote total / reason (x%): −£y" above the VAT lines.
// ─── Quote email HTML ────────────────────────────────────────────────────────
// Light layout with the navy as a header band, so it survives mail apps that
// recolour emails in dark mode (iPhone Gmail inverts dark backgrounds to pale
// blue, which washed out the old all-navy email). The band and the white
// logo are ONE image (public/email-header.png): images are never recoloured,
// so the brand looks the same in light and dark. Apple Mail and others that
// honour prefers-color-scheme get a proper dark version from the CSS below;
// Gmail ignores it and auto-inverts the light body, which reads fine.
// Tables + inline styles for older clients (Outlook).
// assetBase: where the header image is served (the app's own origin).
export function buildQuoteEmailHTML({
  quoteNumber, clientName, contactName, category, totalIncVat, totalExVat = "", discount = null, scopeSummary, personalNote,
  showCourtesyCar, showUnderbody, vehicleMake = "", companyEmail = "", assetBase, quoteUrl,
}) {
  const isAuto = category === "Automotive";
  const toN = v => parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;
  const money2 = n => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const INK = "#1a1a2e", BODY = "#3d4a5c", MUTED = "#6b7a8c", BRAND = "#3a52cc", NAVY = "#1D3461";
  const PANEL = "background:#F4F6FC;border:1px solid #E3E8F2;border-radius:8px;";
  // Outline button: readable on light and on an auto-inverted dark body.
  const outlineBtn = (href, label) => `<a href="${href}" class="sp-btn-outline" style="display:inline-block;background:#ffffff;border:1px solid ${NAVY};color:${NAVY};padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:700;font-size:13px;letter-spacing:0.03em;">${label}</a>`;

  // PLACEHOLDER: the Courtesy Car button links to the marketing site's
  // Automotive page since there's no login-free destination for it yet.
  // Swap this for the dedicated courtesy-car/travel page once it's live.
  const autoExtra = `
    <div class="sp-panel" style="${PANEL}padding:26px 26px 22px;margin:24px 0;">
      <p style="margin:0 0 14px;color:${BRAND};font-weight:700;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;">Turn the Distance into the Drive</p>
      <p class="sp-ink" style="margin:0 0 14px;color:${INK};font-size:14px;line-height:1.75;">Travelling to West Cornwall is worth making a trip of.</p>
      <p class="sp-body" style="margin:0 0 14px;color:${BODY};font-size:14px;line-height:1.75;">While your vehicle is with us for preservation work, a Porsche 996 Cabriolet is yours to use. The B3306 coast road to St Just is on your doorstep. Coastal roads, open stretches, and time to enjoy the journey properly.</p>
      <p class="sp-body" style="margin:0 0 18px;color:${BODY};font-size:14px;line-height:1.75;">St Ives, the Penwith Peninsula, and Land's End are all within easy reach.</p>
      <div class="sp-rule" style="border-top:1px solid #E3E8F2;padding-top:14px;margin-bottom:20px;">
        <p class="sp-ink" style="margin:0 0 6px;color:${INK};font-size:13px;line-height:1.8;">Your vehicle arrives for precision work.</p>
        <p class="sp-ink" style="margin:0 0 6px;color:${INK};font-size:13px;line-height:1.8;">It leaves protected for the next decade.</p>
        <p class="sp-ink" style="margin:0;color:${INK};font-size:13px;line-height:1.8;">You leave having made the journey worthwhile.</p>
      </div>
      <div style="text-align:center;">${outlineBtn("https://www.sublimeprecision.co.uk/automotive", "Ask about the Courtesy Car &rarr;")}</div>
    </div>`;

  const underbodyMailto = `mailto:hello@sublimeprecision.co.uk?subject=${encodeURIComponent(`Underbody protection enquiry – ${quoteNumber}`)}`;
  const underbodyBlock = `
    <div class="sp-panel" style="${PANEL}padding:26px 26px 22px;margin:24px 0;">
      <p style="margin:0 0 14px;color:${BRAND};font-weight:700;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;">While it's on the lift</p>
      <p class="sp-ink" style="margin:0 0 12px;color:${INK};font-size:18px;font-weight:700;line-height:1.4;">Protect the work.</p>
      <p class="sp-body" style="margin:0 0 20px;color:${BODY};font-size:14px;line-height:1.75;">A freshly cleaned underside is the ideal moment to add protection. We offer underbody corrosion protection, applied to clean, dry metal for the best possible bond and the longest-lasting result.</p>
      <div style="text-align:center;">${outlineBtn(underbodyMailto, "Ask about underbody protection &rarr;")}</div>
    </div>`;


  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="light dark">
<meta name="supported-color-schemes" content="light dark">
<title>Your Quotation ${quoteNumber}</title>
<style>
  :root { color-scheme: light dark; supported-color-schemes: light dark; }
  @media (prefers-color-scheme: dark) {
    .sp-bg { background:#0b111b !important; }
    .sp-card { background:#0e1520 !important; border-color:#1e2d42 !important; }
    .sp-panel { background:#141e2e !important; border-color:#1e2d42 !important; }
    .sp-ink { color:#f0ede8 !important; }
    .sp-body { color:#c0cfe0 !important; }
    .sp-muted { color:#8fa3ba !important; }
    .sp-rule { border-color:#1e2d42 !important; }
    .sp-btn-outline { background:#0e1520 !important; border-color:#8fa3ff !important; color:#c9d3ff !important; }
  }
</style>
</head>
<body class="sp-bg" style="margin:0;padding:0;background:#F4F6FC;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" class="sp-bg" bgcolor="#F4F6FC" style="width:100%;table-layout:fixed;background:#F4F6FC;">
<tr><td align="center" style="padding:24px 12px;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" class="sp-card" bgcolor="#ffffff" style="width:100%;max-width:620px;table-layout:fixed;background:#ffffff;border:1px solid #E3E8F2;border-radius:10px;font-family:'DM Sans','Helvetica Neue',Arial,sans-serif;">
    <tr><td style="padding:0;">
      <img src="${assetBase}/email-header.png" width="620" alt="Sublime Precision Cleaning" style="display:block;width:100%;max-width:620px;height:auto;border:0;border-radius:10px 10px 0 0;">
    </td></tr>
    <tr><td style="padding:28px 34px 32px;">
      <p class="sp-muted" style="margin:0 0 24px;text-align:center;color:${MUTED};font-size:12px;letter-spacing:0.12em;text-transform:uppercase;">${isAuto ? "Vehicle Preservation Specialists" : "Professional Dry Ice Blasting"}</p>

      <p class="sp-ink" style="font-size:16px;margin:0 0 12px;color:${INK};">${emailGreeting(contactName, clientName)}</p>
      <p class="sp-body" style="color:${BODY};line-height:1.7;margin:0 0 24px;font-size:15px;">${isAuto
        ? "Thank you for getting in touch. Please find your vehicle preservation quotation below. We've put together a detailed scope based on your enquiry."
        : "Thank you for your enquiry. Please find your quotation from Sublime Precision Cleaning below."
      }</p>
${personalNote && personalNote.trim() ? `
      <p class="sp-ink" style="color:${INK};line-height:1.7;margin:0 0 24px;font-size:15px;">${escapeEmailText(personalNote.trim())}</p>
` : ""}
      <div class="sp-panel" style="${PANEL}padding:20px 22px;margin:0 0 24px;">
        <p class="sp-ink" style="margin:0 0 8px;font-size:14px;color:${INK};"><strong>Quote Reference:</strong> ${quoteNumber}</p>
        <p class="sp-ink" style="margin:0 0 8px;font-size:14px;color:${INK};"><strong>Service Type:</strong> ${category}</p>
        ${discount && discount.amount > 0 ? `<p class="sp-ink" style="margin:0 0 4px;font-size:14px;color:${INK};"><strong>Quote total:</strong> ${money2(discount.subtotalExVat)}</p>
        <p class="sp-ink" style="margin:0 0 4px;font-size:14px;color:${INK};"><strong>${escapeEmailText(discount.reason || "Discount")}${discount.label ? ` (${discount.label})` : ""}:</strong> <span style="white-space:nowrap;">−${money2(discount.amount)}</span></p>` : ""}
        ${totalExVat ? `<p class="sp-ink" style="margin:0 0 4px;font-size:14px;color:${INK};"><strong>Total ex VAT:</strong> ${totalExVat}</p>
        <p class="sp-ink" style="margin:0 0 4px;font-size:14px;color:${INK};"><strong>VAT (20%):</strong> £${(toN(totalIncVat) - toN(totalExVat)).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>` : ""}
        <p class="sp-ink" style="margin:0;font-size:14px;color:${INK};"><strong>Total inc VAT:</strong> ${totalIncVat}</p>
      </div>

      <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:28px auto 6px;">
        <tr><td align="center" bgcolor="${BRAND}" style="background:${BRAND};border-radius:8px;">
          <a href="${quoteUrl}" style="display:inline-block;background:${BRAND};border:1px solid ${BRAND};color:#ffffff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:700;font-size:15px;letter-spacing:0.02em;">View Full Quote &rarr;</a>
        </td></tr>
      </table>
      <p class="sp-muted" style="color:${MUTED};font-size:12px;margin:8px 0 0;text-align:center;">Click above to view your full detailed quotation</p>

      ${showCourtesyCar ? autoExtra : ""}

      <p class="sp-body" style="color:${BODY};line-height:1.7;margin:24px 0;font-size:15px;">${showCourtesyCar
        ? "This quotation is valid for 30 days. If you'd like to discuss the scope, talk through protection options, or find out more about travelling to us, please don't hesitate to get in touch."
        : "This quotation is valid for 30 days. If you have any questions or would like to discuss the scope, please don't hesitate to get in touch."
      }</p>
      ${showUnderbody ? underbodyBlock : ""}
      <p class="sp-ink" style="margin:0;color:${INK};font-size:15px;">Kind regards,<br><strong>Edward</strong><br><span class="sp-muted" style="color:${MUTED};font-size:13px;">Sublime Precision Cleaning</span></p>

      <div class="sp-rule" style="border-top:1px solid #E3E8F2;margin-top:32px;padding-top:16px;text-align:center;">
        <p class="sp-muted" style="margin:0;color:${MUTED};font-size:11px;">Sublime Precision Cleaning &nbsp;·&nbsp; Penwith Peninsula, Cornwall${companyEmail ? ` &nbsp;·&nbsp; ${companyEmail}` : ""}</p>
      </div>
    </td></tr>
  </table>
</td></tr>
</table>
</body>
</html>`;
}

// log: { method, sentBy } - the server records the send in the SendLog once
// the email is accepted (api/send-email.js) and returns the row as logEntry.
export async function sendQuoteEmail({ to, quoteNumber, clientName, contactName, category, totalIncVat, totalExVat = "", discount = null, scopeSummary, bcc, personalNote, blocks = {}, vehicleMake = "", log = null }) {
  const isAuto = category === "Automotive";
  const showCourtesyCar = isAuto && !!blocks.courtesyCar;
  const showUnderbody = isAuto && !!blocks.underbody;

  // Footer contact address - a general enquiries address from Company
  // Settings, never a personal one (this used to hardcode a personal Gmail
  // address directly in a client-facing email). fetchCompanySettings() is
  // cached after the first call in a session, so this costs nothing on
  // repeat sends. Falls back to omitting it entirely rather than showing a
  // blank/broken line if it hasn't been set yet.
  let companyEmail = "";
  try {
    const settings = await fetchCompanySettings();
    companyEmail = settings.companyEmail || "";
  } catch (e) {
    console.error("Failed to load company settings for email footer:", e);
  }

  const html = buildQuoteEmailHTML({
    quoteNumber, clientName, contactName, category, totalIncVat, totalExVat, discount, scopeSummary, personalNote,
    showCourtesyCar, showUnderbody, vehicleMake, companyEmail,
    assetBase: window.location.origin, quoteUrl: quotePageUrl(quoteNumber),
  });

  // No file attachment - the body above already links to the full quote
  // (/quote/:quoteId), and a raw .html attachment only adds spam/security
  // filter risk with no benefit over that link.
  const res = await apiFetch('/api/send-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to,
      subject: `Your Quotation ${quoteNumber} from Sublime Precision Cleaning`,
      html,
      ...((Array.isArray(bcc) ? bcc.length > 0 : !!bcc) && { bcc }),
      ...(log && { log: { quoteNumber, method: log.method, sentBy: log.sentBy } }),
    }),
  });
  if (!res.ok) throw new Error(`Email error: ${res.status}`);
  return await res.json();
}

// ─── Company Settings (admin) ────────────────────────────────────────────────
// Simple key/value pairs on their own sheet tab - business reference data
// only (registered/trading address, company number, VAT number, bank
// details, insurance details, standard terms). Never API keys or service
// credentials - those stay in Vercel env vars, this sheet is reference data
// an admin can edit without a redeploy. Read once and cached in-memory for
// the session; saving overwrites the whole sheet in one go (simplest
// correct upsert for a small fixed set of keys) and refreshes the cache so
// the page reflects the save immediately without a reload.
const COMPANY_SETTINGS_SHEET = "CompanySettings";
export const COMPANY_SETTINGS_FIELDS = [
  // Separate fields rather than one pre-formatted string - the legal entity
  // name (for contracts/invoices) and the trading name (for branding/quote
  // documents) are genuinely different data, e.g. "Way Out West Ltd" vs
  // "Sublime Precision" - composing "X, trading as Y" is a display concern
  // for whatever consumes these, not something to bake into storage.
  { key: "companyName", label: "Company Name (Legal Entity)", type: "text" },
  { key: "tradingAs", label: "Trading As", type: "text" },
  { key: "registeredAddress", label: "Registered Office Address", type: "textarea" },
  { key: "tradingAddress", label: "Trading Address", type: "textarea" },
  { key: "companyNumber", label: "Company Number", type: "text" },
  { key: "vatNumber", label: "VAT Number", type: "text" },
  // General enquiries address, not a personal one - see
  // sendQuoteEmail()'s footer, which used to hardcode a personal Gmail
  // address here instead.
  { key: "companyEmail", label: "Company Email", type: "text" },
  { key: "phoneNumber", label: "Business Phone Number", type: "text" },
  { key: "websiteUrl", label: "Website URL", type: "text" },
  { key: "bankAccountName", label: "Account Name", type: "text", group: "bank" },
  { key: "bankSortCode", label: "Sort Code", type: "text", group: "bank" },
  { key: "bankAccountNumber", label: "Account Number", type: "text", group: "bank" },
  { key: "quoteValidityDays", label: "Quote Validity Period (days)", type: "number" },
  { key: "paymentTerms", label: "Payment Terms", type: "textarea" },
  { key: "defaultVatRate", label: "Default VAT Rate (%)", type: "number" },
  // Admin-only reference links, not business-identity data like everything
  // above - kept in the same sheet/pattern anyway per the "no new tab"
  // brief. Rendered as a clickable link on the page once a URL is saved,
  // not shown at all when blank (no dead links).
  { key: "appServiceStackUrl", label: "App & Service Stack", type: "url", group: "resources" },
  { key: "latestHandoverDocUrl", label: "Latest Handover Doc", type: "url", group: "resources" },
];

let _companySettingsCache = null;

export async function fetchCompanySettings(force) {
  if (_companySettingsCache && !force) return _companySettingsCache;
  if (!SHEET_ID || !API_KEY) {
    throw new Error("Missing REACT_APP_GOOGLE_SHEETS_ID or REACT_APP_GOOGLE_API_KEY");
  }

  const range = `${COMPANY_SETTINGS_SHEET}!A1:B50`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${encodeURIComponent(range)}?key=${API_KEY}`;
  const res = await fetch(url);
  // The sheet doesn't exist until the first save (the Apps Script proxy
  // creates it on write, same as any other sheet) - treat "not found" as
  // "nothing saved yet" rather than an error.
  if (res.status === 400 || res.status === 404) {
    _companySettingsCache = {};
    return _companySettingsCache;
  }
  if (!res.ok) throw new Error(`Couldn't read the Quote Log (error ${res.status}).`);

  const data = await res.json();
  const rows = data.values || [];
  const settings = {};
  rows.slice(1).forEach(([key, value]) => { if (key) settings[key] = value || ""; });
  _companySettingsCache = settings;
  return settings;
}

export async function saveCompanySettings(settings) {
  const SERVICE_URL = "/api/sheets-proxy";
  if (!SERVICE_URL) throw new Error("Missing REACT_APP_SHEETS_PROXY_URL (needed for writes)");

  const rows = [
    ["Key", "Value"],
    ...COMPANY_SETTINGS_FIELDS.map(f => [f.key, settings[f.key] || ""]),
  ];
  const res = await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "replaceAll", sheetName: COMPANY_SETTINGS_SHEET, rows }),
  });
  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  const data = await res.json();
  _companySettingsCache = { ...settings };
  return data;
}

// ─── Documents (admin) ───────────────────────────────────────────────────────
// Policy/legal documents (insurance cover notes, T&Cs, leases,
// certifications, etc.) - an arbitrary, growing list rather than the fixed
// set of fields above, so it lives on its own sheet tab with one row per
// document. The files themselves stay in Google Drive; this only stores
// the description and a link to each one, same "reference data, not the
// document itself" pattern as the Resources links.
//
// Reuses the same generic "replaceAll" action as Company Settings for
// add/delete rather than adding a new per-row-delete Apps Script action -
// this is a small, admin-managed list (not quote volume), so rewriting the
// whole sheet on every change is simple and correct without needing new
// backend surface area.
const DOCUMENTS_SHEET = "Documents";
const DOCUMENTS_HEADERS = ["ID", "Category", "Description", "Drive URL", "Date Added"];

let _documentsCache = null;

export async function fetchDocuments(force) {
  if (_documentsCache && !force) return _documentsCache;
  if (!SHEET_ID || !API_KEY) {
    throw new Error("Missing REACT_APP_GOOGLE_SHEETS_ID or REACT_APP_GOOGLE_API_KEY");
  }

  const range = `${DOCUMENTS_SHEET}!A1:E1000`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${encodeURIComponent(range)}?key=${API_KEY}`;
  const res = await fetch(url);
  // Same as CompanySettings - the sheet doesn't exist until the first
  // document is added, so "not found" just means "no documents yet".
  if (res.status === 400 || res.status === 404) {
    _documentsCache = [];
    return _documentsCache;
  }
  if (!res.ok) throw new Error(`Couldn't read the Quote Log (error ${res.status}).`);

  const data = await res.json();
  const rows = data.values || [];
  const docs = rows.length <= 1 ? [] : rows.slice(1).map(r => ({
    id: r[0] || "",
    category: r[1] || "",
    description: r[2] || "",
    url: r[3] || "",
    dateAdded: r[4] || "",
  }));
  _documentsCache = docs;
  return docs;
}

async function saveDocuments(docs) {
  const SERVICE_URL = "/api/sheets-proxy";
  if (!SERVICE_URL) throw new Error("Missing REACT_APP_SHEETS_PROXY_URL (needed for writes)");

  const rows = [
    DOCUMENTS_HEADERS,
    ...docs.map(d => [d.id, d.category, d.description, d.url, d.dateAdded]),
  ];
  const res = await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "replaceAll", sheetName: DOCUMENTS_SHEET, rows }),
  });
  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  const data = await res.json();
  _documentsCache = docs;
  return data;
}

export async function addDocument({ category, description, url }) {
  const docs = await fetchDocuments();
  const newDoc = {
    // Client-generated - fine for a small admin-managed list with no
    // concurrent-writer risk in practice, no need for a server-computed ID.
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    category: (category || "").trim(),
    description: (description || "").trim(),
    url: (url || "").trim(),
    dateAdded: new Date().toLocaleDateString("en-GB"),
  };
  const updated = [...docs, newDoc];
  await saveDocuments(updated);
  return updated;
}

export async function deleteDocument(id) {
  const docs = await fetchDocuments();
  const updated = docs.filter(d => d.id !== id);
  await saveDocuments(updated);
  return updated;
}

// ─── Suppliers (admin) ────────────────────────────────────────────────────────
// Dry-ice (and future) suppliers for the Purchase Order system - own sheet
// tab, one row per supplier, same "fetch full list, mutate client-side,
// replaceAll" pattern as Documents. Unlike Documents, records here have
// many fields and get edited in place (not just added/removed), so each
// supplier is saved individually (saveSupplier upserts one record into the
// full list) rather than the page holding one big unsaved draft of all of
// them - editing Nippon's price and adding BOC's details can't stomp on
// each other this way.
const SUPPLIERS_SHEET = "Suppliers";
const SUPPLIERS_HEADERS = [
  "ID", "Name", "Order Email", "Account Number", "Products Supplied",
  "Default Lead Time (days)", "Order Cutoff Time",
  "Current Cost Price (GBP/kg)", "Price Last Updated",
  "Bin Rental (GBP/bin)", "Delivery Surcharge (GBP/bin, Yard rate)",
  "Minimum Order (kg)", "Bin Capacities (JSON)",
  "Payment Terms", "Yard Delivery Address", "Active", "Notes",
  "Primary", "Delivered Price Tiers (JSON)", "Delivered Extras (JSON)", "Tiers Valid To",
];
// £/kg, not £/tonne - matches the unit both calculators read this field in
// directly (see useDryIceSupplier in App.jsx), so no unit conversion is
// needed at the point of use. Convert on entry if a supplier's quote comes
// in £/tonne (divide by 1000).
const SUPPLIERS_PRICE_UNIT = "GBP/kg";
// How many days old Price Last Updated can get before Company Settings
// warns it might be stale - a named constant so tightening it later is a
// one-line change, not a hunt through the page for a magic number.
export const SUPPLIER_PRICE_STALE_DAYS = 90;

// ─── Dates ───────────────────────────────────────────────────────────────────
// The sheet mixes two date formats: en-GB "DD/MM/YYYY" text (Date Created,
// Date Updated, Date Added, Price Last Updated - all written via
// toLocaleDateString("en-GB")) and ISO (<input type="date"> "YYYY-MM-DD"
// values like job.jobDate / requestedDeliveryDate, plus full ISO timestamps
// like poSentTimestamp). Everything that reads a date goes through
// parseDate so both are handled the same way everywhere.
//
// Returns a local-midnight Date (time of day dropped - these are all
// calendar dates), or null for blank/unparseable/impossible dates like
// 31/02/2026. Never uses new Date("YYYY-MM-DD") directly, which parses as
// UTC midnight and lands on the previous day west of Greenwich.
export function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const s = String(value).trim();
  let y, m, d;
  let match = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  } else if ((match = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) {
    [d, m, y] = [Number(match[1]), Number(match[2]), Number(match[3])];
  } else if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const t = new Date(s);
    return isNaN(t.getTime()) ? null : new Date(t.getFullYear(), t.getMonth(), t.getDate());
  } else {
    return null;
  }
  const date = new Date(y, m - 1, d);
  // Rejects rollover (31/02 -> 3 March) rather than silently accepting it.
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

// Date -> "YYYY-MM-DD" in local time (the <input type="date"> format, and
// how job dates are stored in PO Data). Accepts anything parseDate does.
export function toISODate(value) {
  const date = parseDate(value);
  if (!date) return "";
  const pad = n => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Date strings (either format) -> days elapsed, or null if unparseable/blank.
// Shared by Company Settings' own staleness warning and the calculators'
// dry-ice pricing.
export function daysSince(dateStr) {
  const then = parseDate(dateStr);
  if (!then) return null;
  return Math.floor((Date.now() - then.getTime()) / 86400000);
}

// Active suppliers whose Products Supplied names dry ice - what the
// calculators source their live price/delivery/bin-hire figures from
// instead of a hardcoded number, so a Suppliers-tab price update is
// immediately live everywhere it's used.
export function findDryIceSuppliers(suppliers) {
  return suppliers.filter(s => s.active && /dry ice/i.test(s.productsSupplied || ""));
}

// Lowest current price among active dry-ice suppliers - the fallback when
// no supplier is ticked Primary.
export function cheapestDryIceSupplier(dryIceSuppliers) {
  if (!dryIceSuppliers.length) return null;
  return [...dryIceSuppliers].sort((a, b) =>
    (Number(a.currentCostPricePerKg) || Infinity) - (Number(b.currentCostPricePerKg) || Infinity)
  )[0];
}

// The default dry-ice supplier: the one ticked Primary in Admin, else the
// cheapest by £/kg.
export function primaryDryIceSupplier(dryIceSuppliers) {
  return dryIceSuppliers.find(s => s.primary) || cheapestDryIceSupplier(dryIceSuppliers);
}

// ─── Delivered price tiers ───────────────────────────────────────────────────
// Some suppliers (Apsley Farms) price dry ice as fixed delivered tiers - e.g.
// 40kg £140, 100kg £250 - with delivery and collection included, so there's
// no £/kg, bin hire or delivery surcharge. Stored as JSON lists:
// deliveredTiers [{kg, price}] and deliveredExtras [{label, price}] (optional
// charges such as a Saturday delivery, ticked on the PO only). All ex VAT.
export function deliveredTiersOf(supplier) {
  return (supplier?.deliveredTiers || [])
    .map(t => ({ kg: Number(t.kg), price: Number(t.price) }))
    .filter(t => t.kg > 0 && t.price > 0);
}
export function hasDeliveredTiers(supplier) {
  return deliveredTiersOf(supplier).length > 0;
}
// True once the tiers' Valid To date has passed (they're still used, with a warning).
export function deliveredTiersExpired(supplier) {
  const to = parseDate(supplier?.tiersValidTo);
  return !!to && to.getTime() < parseDate(new Date()).getTime();
}

// The cheapest tier, or combination of tiers, whose kg covers quantityKg:
// { tiers: [{kg, price, count}], orderedKg, totalCost }, or null with no
// quantity or no tiers. On equal cost, the smaller total kg wins.
// e.g. Apsley: 22kg -> 1 x 40kg £140; 110kg -> 1 x 200kg £350 (cheaper than
// 100kg + 40kg at £390); 275kg -> 1 x 300kg £450.
export function chooseDeliveredTiers(quantityKg, tiers) {
  const qty = Number(quantityKg);
  const list = Array.isArray(tiers) ? tiers.filter(t => t.kg > 0 && t.price > 0) : [];
  if (!(qty > 0) || !list.length) return null;
  // Whole kg; tier sizes are whole kg too. Cheapest exact total for every kg
  // up to the need plus the largest tier (no cheaper cover can be bigger).
  const need = Math.ceil(qty - 1e-9);
  const limit = need + Math.max(...list.map(t => Math.ceil(t.kg)));
  const best = new Array(limit + 1).fill(Infinity);
  const pick = new Array(limit + 1).fill(null);
  best[0] = 0;
  for (let w = 1; w <= limit; w++) {
    for (const t of list) {
      const k = Math.ceil(t.kg);
      if (k <= w && best[w - k] + t.price < best[w] - 1e-9) { best[w] = best[w - k] + t.price; pick[w] = t; }
    }
  }
  let at = -1;
  for (let w = need; w <= limit; w++) if (best[w] < Infinity && (at < 0 || best[w] < best[at] - 1e-9)) at = w;
  if (at < 0) return null;
  const counts = new Map();
  for (let w = at; w > 0; w -= Math.ceil(pick[w].kg)) counts.set(pick[w], (counts.get(pick[w]) || 0) + 1);
  const chosen = [...counts].map(([t, count]) => ({ kg: t.kg, price: t.price, count })).sort((a, b) => b.kg - a.kg);
  return { tiers: chosen, orderedKg: chosen.reduce((n, t) => n + t.kg * t.count, 0), totalCost: best[at] };
}

// "1 × 200kg delivered" / "1 × 200kg + 1 × 40kg delivered".
export function tierPlanLabel(tierPlan) {
  return (tierPlan?.tiers || []).map(t => `${t.count} × ${t.kg}kg`).join(" + ") + " delivered";
}

// Dry ice cost for a calculator: { cost, binsNeeded, binHire, tierPlan }.
// A tiered supplier prices from its tiers (no bins, hire or delivery);
// otherwise £/kg + delivery surcharge and hire per bin, as before.
export function dryIceCostFor(supplier, adjustedKg, binCapacityKg) {
  const kg = Number(adjustedKg) || 0;
  if (hasDeliveredTiers(supplier)) {
    const tierPlan = kg > 0 ? chooseDeliveredTiers(kg, deliveredTiersOf(supplier)) : null;
    return { cost: tierPlan ? tierPlan.totalCost : 0, binsNeeded: 0, binHire: 0, tierPlan };
  }
  const binsNeeded = kg > 0 && binCapacityKg > 0 ? Math.ceil(kg / binCapacityKg) : 0;
  const binHire = binsNeeded * (Number(supplier?.binRentalPerBin) || 0);
  const cost = kg > 0 ? kg * (Number(supplier?.currentCostPricePerKg) || 0) + (Number(supplier?.deliverySurchargePerBin) || 0) * binsNeeded + binHire : 0;
  return { cost, binsNeeded, binHire, tierPlan: null };
}

// Bin Capacities is stored as a JSON string in its own cell (a repeatable
// {label, capacity_kg} list needs real structure, not free text - the
// point of this field is letting the app pick a bin size programmatically
// instead of a human reading it off Products Supplied), parsed back into a
// real array on read so every consumer (Company Settings' editor, the PO
// cost calculation below) works with actual objects, never a raw string.
function parseBinCapacities(json) {
  return parseJsonList(json);
}
function parseJsonList(json) {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function supplierRowToObject(r) {
  return {
    id: r[0] || "",
    name: r[1] || "",
    orderEmail: r[2] || "",
    accountNumber: r[3] || "",
    productsSupplied: r[4] || "",
    defaultLeadTimeDays: r[5] || "",
    orderCutoffTime: r[6] || "",
    currentCostPricePerKg: r[7] || "",
    priceLastUpdated: r[8] || "",
    binRentalPerBin: r[9] || "",
    deliverySurchargePerBin: r[10] || "",
    minimumOrderKg: r[11] || "",
    binCapacities: parseBinCapacities(r[12]),
    paymentTerms: r[13] || "",
    yardDeliveryAddress: r[14] || "",
    active: r[15] === "Yes",
    notes: r[16] || "",
    primary: r[17] === "Yes",
    deliveredTiers: parseJsonList(r[18]),
    deliveredExtras: parseJsonList(r[19]),
    tiersValidTo: r[20] || "",
  };
}

function supplierObjectToRow(s) {
  return [
    s.id, s.name, s.orderEmail, s.accountNumber, s.productsSupplied,
    s.defaultLeadTimeDays, s.orderCutoffTime,
    s.currentCostPricePerKg, s.priceLastUpdated,
    s.binRentalPerBin, s.deliverySurchargePerBin, s.minimumOrderKg,
    JSON.stringify(s.binCapacities || []),
    s.paymentTerms, s.yardDeliveryAddress, s.active ? "Yes" : "No", s.notes,
    s.primary ? "Yes" : "No",
    JSON.stringify(s.deliveredTiers || []), JSON.stringify(s.deliveredExtras || []), s.tiersValidTo || "",
  ];
}

// Seed defaults - only ever shown when the Suppliers sheet is empty (fresh
// install), and only ever saved once an admin actually clicks Save on them,
// same as every other write path in this app. Nippon's data is real
// (confirmed 14 Sept 2026); BOC is a placeholder left inactive until its
// real details are on file, per the brief.
function defaultSuppliers(tradingAddress) {
  const yardAddress = tradingAddress || "Unit 1C, Guildford Road Industrial Estate, Hayle, Cornwall, TR27 4QZ";
  const today = new Date().toLocaleDateString("en-GB");
  return [
    {
      id: "nippon-seed",
      name: "Nippon Gases",
      orderEmail: "", // not supplied - flagged as missing, needs filling in
      accountNumber: "419539",
      productsSupplied: "Dry ice pellets (3mm blast) — 300-series 210kg / 500-series 325kg bins, 100kg minimum order",
      defaultLeadTimeDays: "2",
      orderCutoffTime: "11:00",
      currentCostPricePerKg: (1473 / 1000).toString(), // £1,473/tonne as of 14 Sept 2026
      priceLastUpdated: "14/09/2026",
      binRentalPerBin: "10.00",
      // Nippon's own pricing varies £77-£129 by postcode zone - this is
      // only the confirmed rate for the Yard address (TR27). See the
      // "Site" delivery-destination warning wired in at Stage 5 for where
      // this gets flagged as an estimate rather than a locked figure.
      deliverySurchargePerBin: "129.00", // TR27 zone rate - confirm if delivering elsewhere
      minimumOrderKg: "100",
      binCapacities: [
        { label: "300-series", capacity_kg: 210 },
        { label: "500-series", capacity_kg: 325 },
      ],
      paymentTerms: "Immediate — bank or card, no credit account",
      yardDeliveryAddress: yardAddress,
      active: true,
      notes: "",
    },
    {
      id: "boc-seed",
      name: "BOC",
      orderEmail: "",
      accountNumber: "",
      productsSupplied: "",
      defaultLeadTimeDays: "",
      orderCutoffTime: "",
      currentCostPricePerKg: "",
      priceLastUpdated: "",
      binRentalPerBin: "",
      deliverySurchargePerBin: "",
      minimumOrderKg: "",
      binCapacities: [],
      paymentTerms: "",
      yardDeliveryAddress: yardAddress,
      active: false, // placeholder only - details not yet on file
      notes: "Details not yet on file — placeholder row.",
    },
  ];
}

let _suppliersCache = null;

export async function fetchSuppliers(force) {
  if (_suppliersCache && !force) return _suppliersCache;
  if (!SHEET_ID || !API_KEY) {
    throw new Error("Missing REACT_APP_GOOGLE_SHEETS_ID or REACT_APP_GOOGLE_API_KEY");
  }

  const range = `${SUPPLIERS_SHEET}!A1:U1000`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${encodeURIComponent(range)}?key=${API_KEY}`;
  const res = await fetch(url);
  if (res.status === 400 || res.status === 404) {
    // Sheet doesn't exist yet - this is the one place that returns seed
    // data instead of an empty list, so a fresh install shows Nippon's real
    // data and a BOC placeholder ready to review/save rather than a blank
    // page. Only ever persisted once the admin actually saves a row.
    const settings = await fetchCompanySettings().catch(() => ({}));
    _suppliersCache = defaultSuppliers(settings.tradingAddress);
    return _suppliersCache;
  }
  if (!res.ok) throw new Error(`Couldn't read the Quote Log (error ${res.status}).`);

  const data = await res.json();
  const rows = data.values || [];
  const suppliers = rows.length <= 1 ? [] : rows.slice(1).map(supplierRowToObject);
  _suppliersCache = suppliers;
  return suppliers;
}

async function saveSuppliersList(suppliers) {
  const SERVICE_URL = "/api/sheets-proxy";
  if (!SERVICE_URL) throw new Error("Missing REACT_APP_SHEETS_PROXY_URL (needed for writes)");

  const rows = [SUPPLIERS_HEADERS, ...suppliers.map(supplierObjectToRow)];
  const res = await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "replaceAll", sheetName: SUPPLIERS_SHEET, rows }),
  });
  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  const data = await res.json();
  _suppliersCache = suppliers;
  return data;
}

// Upserts one supplier into the full list and saves. Auto-stamps Price
// Last Updated to today whenever the cost price actually changed from what
// was last saved - not on every save, so re-saving unrelated fields (e.g.
// a corrected phone number) doesn't make the price look freshly checked
// when it wasn't.
// Delivered tiers count as the price too. Only one supplier can be Primary:
// ticking it on one unticks it everywhere else, in the same save.
export async function saveSupplier(supplier) {
  const current = await fetchSuppliers();
  const existing = current.find(s => s.id === supplier.id);
  const priceChanged = !existing || String(existing.currentCostPricePerKg) !== String(supplier.currentCostPricePerKg)
    || JSON.stringify(existing.deliveredTiers || []) !== JSON.stringify(supplier.deliveredTiers || []);
  const toSave = {
    ...supplier,
    priceLastUpdated: priceChanged ? new Date().toLocaleDateString("en-GB") : supplier.priceLastUpdated,
  };
  const others = supplier.primary ? current.map(s => (s.primary ? { ...s, primary: false } : s)) : current;
  const updated = existing
    ? others.map(s => (s.id === supplier.id ? toSave : s))
    : [...others, toSave];
  await saveSuppliersList(updated);
  return updated;
}

export async function deleteSupplier(id) {
  const current = await fetchSuppliers();
  const updated = current.filter(s => s.id !== id);
  await saveSuppliersList(updated);
  return updated;
}

// Picks a bin size for a given quantity: the smallest single bin that
// covers it in one go where one exists, otherwise as many of the largest
// bin as needed. Used by computePOTotalCost below, and by the quantity-
// rounding step in Stage 6 (same "pick the smallest bin that covers it"
// logic scoped there) - one function, not two copies to keep in sync.
export function chooseBinPlan(quantityKg, binCapacities) {
  const qty = Number(quantityKg);
  if (!qty || !Array.isArray(binCapacities) || binCapacities.length === 0) return null;
  const sorted = [...binCapacities].sort((a, b) => a.capacity_kg - b.capacity_kg);
  const singleBin = sorted.find(b => b.capacity_kg >= qty);
  if (singleBin) return { label: singleBin.label, capacityKg: singleBin.capacity_kg, binsRequired: 1 };
  const largest = sorted[sorted.length - 1];
  return { label: largest.label, capacityKg: largest.capacity_kg, binsRequired: Math.ceil(qty / largest.capacity_kg) };
}

// Total Cost = (price x quantity) + (bin rental x bins) + (delivery
// surcharge x bins) - the delivery surcharge is charged per-container per
// Nippon's own quote wording, not once per order; that reading lines up
// with the single 100kg/1-bin example given but hasn't actually been
// tested against a multi-bin order - worth confirming with Nippon before
// this is fully trusted on a larger job. Returns null if there's not
// enough data (no supplier, no quantity, or the supplier has no bin
// capacities set up yet) rather than guessing at a bin plan.
//
// A supplier with delivered tiers (hasDeliveredTiers) is priced from them
// instead: the cheapest tier(s) covering the quantity, plus any ticked
// extras (labels from supplier.deliveredExtras), with no bin or surcharge.
// Both shapes carry orderedKg, planLabel and lines ([label, £]) for display.
export function computePOTotalCost({ quantityOrderedKg, supplier, extras = [] }) {
  if (!supplier || !quantityOrderedKg) return null;
  if (hasDeliveredTiers(supplier)) {
    const tierPlan = chooseDeliveredTiers(quantityOrderedKg, deliveredTiersOf(supplier));
    if (!tierPlan) return null;
    const extraLines = (supplier.deliveredExtras || [])
      .filter(x => extras.includes(x.label) && Number(x.price) > 0)
      .map(x => [x.label, Number(x.price)]);
    const extrasCost = extraLines.reduce((n, [, v]) => n + v, 0);
    return {
      tiered: true,
      tierPlan,
      plan: null,
      binsRequired: 0,
      orderedKg: tierPlan.orderedKg,
      planLabel: tierPlan.tiers.map(t => `${t.count} × ${t.kg}kg delivered (£${t.price.toFixed(2)} each)`).join(" + "),
      productCost: tierPlan.totalCost,
      rentalCost: 0,
      deliveryCost: 0,
      extrasCost,
      extras: extraLines.map(([label, price]) => ({ label, price })),
      lines: [[`Dry ice, ${tierPlanLabel(tierPlan)}`, tierPlan.totalCost], ...extraLines],
      totalCost: tierPlan.totalCost + extrasCost,
    };
  }
  const plan = chooseBinPlan(quantityOrderedKg, supplier.binCapacities);
  if (!plan) return null;
  const pricePerKg = Number(supplier.currentCostPricePerKg) || 0;
  const binRental = Number(supplier.binRentalPerBin) || 0;
  const deliverySurcharge = Number(supplier.deliverySurchargePerBin) || 0;
  const productCost = pricePerKg * Number(quantityOrderedKg);
  const rentalCost = binRental * plan.binsRequired;
  const deliveryCost = deliverySurcharge * plan.binsRequired;
  return {
    plan,
    binsRequired: plan.binsRequired,
    orderedKg: plan.binsRequired * plan.capacityKg,
    planLabel: `${plan.binsRequired} × ${plan.label} (${plan.capacityKg}kg each)`,
    productCost,
    rentalCost,
    deliveryCost,
    lines: [["Product", productCost], ["Bin/Container Rental", rentalCost], ["Delivery Surcharge", deliveryCost]],
    totalCost: productCost + rentalCost + deliveryCost,
  };
}

// ─── Purchase Orders (dry-ice) ────────────────────────────────────────────────
// One JSON blob per quote row (column X, "PO Data") rather than individual
// lettered columns - QuoteLog is already at column W and the existing
// updateCells action only supports single-letter columns
// (col.charCodeAt(0)-64 in SheetsProxy.gs), so this follows the same
// pattern as the existing State JSON column rather than adding more
// lettered ones. Grows to hold PO status/tracking fields in later build
// stages (draft/approve/send, quantities, surplus) - only supplierId,
// deliveryDestination, and requestedDeliveryDate are written at this stage.
export function parsePOData(quote) {
  if (!quote || !quote["PO Data"]) return {};
  try { return JSON.parse(quote["PO Data"]) || {}; } catch (e) { return {}; }
}

// Pulls the calculator's own dry-ice requirement out of a saved quote's
// State JSON (costBreakdown.dryIceKgAdjusted/dryIceKgRequired, written by
// both calculators in App.jsx) so the Raise PO screen can default its
// quantity to what the job actually needs instead of a manually re-typed
// guess. Prefers the wastage-adjusted figure - that's the real amount to
// buy, not the pre-wastage production figure. Returns null for quotes
// saved before this was tracked, or anything unparseable/zero - callers
// fall back to a manual field rather than blocking the PO flow on it.
export function extractCalculatorDryIceKg(quote) {
  if (!quote || !quote["State JSON"]) return null;
  try {
    const sj = JSON.parse(quote["State JSON"]);
    const cb = sj?.costBreakdown;
    const val = cb?.dryIceKgAdjusted ?? cb?.dryIceKgRequired;
    return (val != null && Number(val) > 0) ? Number(val) : null;
  } catch (e) {
    return null;
  }
}

// Rounds a dry-ice kg figure to 1 decimal for display, stripping a trailing
// ".0" - the calculator's raw wastage-adjusted output is a float with
// float-arithmetic noise (e.g. 44.35200000000001), which isn't meaningfully
// more accurate for ordering purposes than 44.4kg. Shared by the Raise PO
// screen and the PO email template so both show the same clean number.
export function fmtKg(n) {
  const rounded = Math.round(Number(n) * 10) / 10;
  return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1);
}

export async function savePODetails(rowIndex, poData) {
  const SERVICE_URL = "/api/sheets-proxy";
  if (!SERVICE_URL) throw new Error("Missing REACT_APP_SHEETS_PROXY_URL");
  const res = await apiFetch(SERVICE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: "updateCells",
      sheetName: SHEET_NAME,
      rowIndex,
      cells: [{ col: COLUMNS.PO_DATA, value: JSON.stringify(poData) }],
    }),
  });
  if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
  return await res.json();
}

// ─── Job dates ───────────────────────────────────────────────────────────────
// jobStartDate/jobEndDate ("YYYY-MM-DD") live in the same PO Data JSON as
// requestedDeliveryDate rather than in a new sheet column. They're
// scheduling info, not part of the quote itself, so editing them must never
// go through updateQuoteRow/appendAmendment/updateQuoteStatus - saveJobDates
// writes column X only and leaves Status and Date Updated untouched.
const JOB_DATE_FIELDS = ["jobStartDate", "jobEndDate"];

// The calculator's own "Scheduled Date" (State JSON job.jobDate), if any.
export function getScheduledDate(quote) {
  if (!quote || !quote["State JSON"]) return "";
  try {
    return toISODate(JSON.parse(quote["State JSON"])?.job?.jobDate);
  } catch (e) {
    return "";
  }
}

// Effective job dates for the calendar: start = jobStartDate, else the
// quote's Scheduled Date; end = jobEndDate, else start. An end before the
// start is treated as a one-day job rather than dropped. Both "" if the job
// has no date at all (i.e. it's Unscheduled).
export function getJobDates(quote) {
  const po = parsePOData(quote);
  const start = toISODate(po.jobStartDate) || scheduledDateUnlessCleared(quote, po);
  if (!start) return { start: "", end: "" };
  const end = toISODate(po.jobEndDate);
  return { start, end: end && end >= start ? end : start };
}

// The quote's Scheduled Date, unless Edit dates cleared it: a cleared Job
// start saves scheduledDateCleared = that date, so the job reads as
// unscheduled. Only that exact date is hidden - if an amendment gives the
// quote a different Scheduled Date, it shows again.
function scheduledDateUnlessCleared(quote, po) {
  const scheduled = getScheduledDate(quote);
  return scheduled && toISODate(po.scheduledDateCleared) === scheduled ? "" : scheduled;
}

// Carries a row's saved job dates onto a replacement PO Data object - for
// the PO writes (Save PO Details, Raise PO) that build column X from
// scratch, so they don't wipe job dates saved separately.
export function withJobDates(existingPOData, poData) {
  const carried = {};
  [...JOB_DATE_FIELDS, "scheduledDateCleared"].forEach(k => { if (existingPOData?.[k]) carried[k] = existingPOData[k]; });
  return { ...carried, ...poData };
}

// Saves just the job dates, merged onto whatever PO Data the row already
// has. Blank values remove the field. A blank Job start on a quote that has
// a Scheduled Date means "unscheduled": it records that date as cleared, so
// it doesn't come back (see scheduledDateUnlessCleared). Setting a start
// date removes the marker. Returns the saved PO Data object.
export async function saveJobDates(quote, { jobStartDate, jobEndDate }) {
  const poData = { ...parsePOData(quote) };
  const dates = { jobStartDate: toISODate(jobStartDate), jobEndDate: toISODate(jobEndDate) };
  JOB_DATE_FIELDS.forEach(k => { if (dates[k]) poData[k] = dates[k]; else delete poData[k]; });
  const scheduled = getScheduledDate(quote);
  if (!dates.jobStartDate && scheduled) poData.scheduledDateCleared = scheduled;
  else delete poData.scheduledDateCleared;
  await savePODetails(quote._rowIndex, poData);
  return poData;
}

// Marks a sent PO as confirmed by the supplier - merged onto the row's
// existing PO Data (column X only), so the sent snapshot, job dates and
// everything else stay as they were. PODueCheck.gs skips "confirmed" the
// same way it skips "sent". Returns the saved PO Data object.
export async function markPOConfirmed(quote, confirmedBy) {
  const poData = {
    ...parsePOData(quote),
    poStatus: "confirmed",
    poConfirmedAt: new Date().toISOString(),
    poConfirmedBy: confirmedBy || "",
  };
  await savePODetails(quote._rowIndex, poData);
  return poData;
}

// Order-by Date = Requested Delivery Date - lead time, in calendar days
// (not business days), rolled BACK to the preceding Friday if that lands on
// a weekend - never pushed later, only earlier, so the order is always
// placed in time even in the worst case. dateStr is "YYYY-MM-DD" (native
// <input type="date"> format). Mirrored (not shared - different runtime)
// by the Apps Script daily trigger in a later stage; keep both in sync if
// this logic ever changes.
export function computeOrderByDate(requestedDeliveryDate, leadTimeDays) {
  if (!requestedDeliveryDate || leadTimeDays == null || leadTimeDays === "") return null;
  const d = parseDate(requestedDeliveryDate);
  if (!d) return null;
  d.setDate(d.getDate() - Number(leadTimeDays));
  const day = d.getDay(); // 0 = Sunday, 6 = Saturday
  if (day === 0) d.setDate(d.getDate() - 2);
  else if (day === 6) d.setDate(d.getDate() - 1);
  return d;
}

// Sends the actual PO email via the same Resend-backed /api/send-email
// endpoint every other outbound email in this app uses - no new email
// infrastructure. Only ever called after an admin approves on the Raise PO
// screen; never fires from a draft alone.
export async function sendPurchaseOrderEmail({
  to, poRef, supplierName, accountNumber, deliveryAddress,
  requestedDeliveryDate, quantityKg, binPlan, approverName, companyPhone, companyEmail, bcc,
  unitCostPerKg, productCost, rentalCost, deliveryCost, totalCost, paymentTerms,
  tierPlan = null, extras = [],
  companyName, tradingAs, registeredAddress, companyNumber, vatNumber,
}) {
  const fmtDateLong = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—";
  const dateLabel = fmtDateLong(requestedDeliveryDate);
  // "Now", not a value passed by the caller - the issue date is always the
  // moment the email actually goes out, not whenever the admin opened the
  // Raise PO screen.
  const issueDateLabel = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  // A delivered-tier order (tierPlan) is for the tier's kg, priced per tier
  // with delivery and collection included: no unit price, bin or surcharge.
  const binLabel = tierPlan ? "" : binPlan ? ` (${binPlan.binsRequired} x ${binPlan.label})` : "";
  const orderQty = tierPlan ? `${fmtKg(tierPlan.orderedKg)}kg (${tierPlanLabel(tierPlan)})` : `${fmtKg(quantityKg)}kg${binLabel}`;
  const signoffContact = [companyPhone, companyEmail].filter(Boolean).join(" / ");
  const money = (n) => n != null ? `£${Number(n).toFixed(2)}` : "—";

  // Brand-forward header (Sublime Precision), legal registration details in
  // small print in the footer only - matches the existing Xero invoice
  // convention, where Way Out West Ltd (the registered entity) is kept out
  // of the prominent branding and only appears in required small print.
  const brandName = tradingAs || "Sublime Precision Cleaning";
  const legalLine = [
    companyName ? (tradingAs && tradingAs !== companyName ? `${companyName}, trading as ${tradingAs}` : companyName) : "",
    companyNumber ? `Company No. ${companyNumber}` : "",
    vatNumber ? `VAT No. ${vatNumber}` : "",
  ].filter(Boolean).join(" &middot; ");
  const addressHtml = registeredAddress ? registeredAddress.replace(/\n/g, "<br>") : "";

  const html = `
    <div style="font-family:'DM Sans',sans-serif;max-width:600px;margin:0 auto;background:#ffffff;color:#1a1a2e;padding:36px;border:1px solid #e0e6f0;border-radius:8px;">
      <img src="https://sublime-quotes-git-main-sublime2.vercel.app/logo-dark.png" alt="${brandName}" style="height:36px;width:auto;margin-bottom:16px;"/>
      <div style="margin-bottom:24px;">
        <p style="margin:0;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#7a8fa8;">Purchase Order</p>
        <p style="margin:4px 0 0;font-size:22px;font-weight:700;color:#1D3461;">${poRef}</p>
      </div>
      <p style="font-size:14px;margin:0 0 16px;">Dear ${supplierName} team,</p>
      <p style="font-size:14px;color:#333;line-height:1.6;margin:0 0 24px;">Please find our order details below.</p>
      <table style="width:100%;table-layout:fixed;border-collapse:collapse;font-size:13px;margin-bottom:24px;">
        <colgroup><col style="width:42%;"><col style="width:58%;"></colgroup>
        <tr><td style="padding:6px 0;color:#7a8fa8;">Account / Customer Reference</td><td style="padding:6px 0;text-align:right;font-weight:600;word-break:break-word;">${accountNumber || "—"}</td></tr>
        <tr><td style="padding:6px 0;color:#7a8fa8;">Delivery Address</td><td style="padding:6px 0;text-align:right;font-weight:600;word-break:break-word;">${deliveryAddress}</td></tr>
        <tr><td style="padding:6px 0;color:#7a8fa8;">PO Issue Date</td><td style="padding:6px 0;text-align:right;font-weight:600;word-break:break-word;">${issueDateLabel}</td></tr>
        <tr><td style="padding:6px 0;color:#7a8fa8;">Requested Delivery Date</td><td style="padding:6px 0;text-align:right;font-weight:600;word-break:break-word;">${dateLabel}</td></tr>
      </table>
      <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:24px;">
        <tr style="background:#1D3461;color:#fff;">
          <th style="padding:8px 12px;text-align:left;font-size:11px;letter-spacing:0.05em;text-transform:uppercase;">Product</th>
          <th style="padding:8px 12px;text-align:right;font-size:11px;letter-spacing:0.05em;text-transform:uppercase;">Quantity</th>
        </tr>
        <tr>
          <td style="padding:8px 12px;border-bottom:1px solid #eef0f6;">Dry ice pellets (3mm blast)</td>
          <td style="padding:8px 12px;border-bottom:1px solid #eef0f6;text-align:right;">${orderQty}</td>
        </tr>
      </table>
      <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:8px;">
        ${tierPlan ? `${tierPlan.tiers.map(t => `<tr><td style="padding:6px 0;color:#7a8fa8;">${t.count} x ${t.kg}kg delivered (excl. VAT, delivery and collection included)</td><td style="padding:6px 0;text-align:right;font-weight:600;">${money(t.price * t.count)}</td></tr>`).join("")}
        ${extras.map(x => `<tr><td style="padding:6px 0;color:#7a8fa8;">${x.label}</td><td style="padding:6px 0;text-align:right;font-weight:600;">${money(x.price)}</td></tr>`).join("")}` : `<tr><td style="padding:6px 0;color:#7a8fa8;">Unit Price (excl. VAT)</td><td style="padding:6px 0;text-align:right;font-weight:600;">${unitCostPerKg != null ? `£${Number(unitCostPerKg).toFixed(3)}/kg` : "—"}</td></tr>
        <tr><td style="padding:6px 0;color:#7a8fa8;">Product</td><td style="padding:6px 0;text-align:right;font-weight:600;">${money(productCost)}</td></tr>
        <tr><td style="padding:6px 0;color:#7a8fa8;">Bin/Container Rental</td><td style="padding:6px 0;text-align:right;font-weight:600;">${money(rentalCost)}</td></tr>
        <tr><td style="padding:6px 0;color:#7a8fa8;">Delivery Surcharge</td><td style="padding:6px 0;text-align:right;font-weight:600;">${money(deliveryCost)}</td></tr>`}
        <tr><td style="padding:10px 0 0;color:#1D3461;font-weight:700;border-top:1px solid #eef0f6;">Total Cost (excl. VAT)</td><td style="padding:10px 0 0;text-align:right;font-weight:700;color:#1D3461;border-top:1px solid #eef0f6;">${money(totalCost)}</td></tr>
      </table>
      ${paymentTerms ? `<p style="font-size:12px;color:#7a8fa8;margin:0 0 24px;">Payment Terms: ${paymentTerms}</p>` : ""}
      <p style="font-size:14px;margin:0 0 16px;">Kind regards,<br><strong>${approverName}</strong><br><span style="color:#7a8fa8;font-size:13px;">${brandName}${signoffContact ? ` — ${signoffContact}` : ""}</span></p>
      <p style="font-size:12px;color:#7a8fa8;margin:0 0 24px;">Please confirm receipt of this order at your earliest convenience.</p>
      <div style="padding-top:16px;border-top:1px solid #eef0f6;font-size:11px;color:#9aa3b5;line-height:1.6;">
        ${legalLine}${legalLine && addressHtml ? "<br>" : ""}${addressHtml}
      </div>
    </div>
  `;

  const res = await apiFetch('/api/send-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to,
      subject: `Purchase Order ${poRef} — Sublime Precision — ${dateLabel}`,
      html,
      ...((Array.isArray(bcc) ? bcc.length > 0 : !!bcc) && { bcc }),
    }),
  });
  if (!res.ok) throw new Error(`Email error: ${res.status}`);
  return await res.json();
}
